import { useEffect, useState } from 'react';
import { CHECKBOX_STATUS } from 'ui/checkbox';
import { isValidSplitPercentage, splitPercentageToString } from './utils';

const DEFAULT_SPLIT_GROUPS = new Array(2).fill().map(() => ({ splitPercentage: 0.5 }));

const getInitialState = (initialSplitGroups) =>
  (initialSplitGroups.length >= 2 ? initialSplitGroups : DEFAULT_SPLIT_GROUPS).map(
    (splitGroup) => ({
      splitPercentage: splitPercentageToString(splitGroup.splitPercentage, 2),
    }),
  );

const getFinalValueInEvenDistribution = (numGroups, individualValue) => {
  /*
    Ensures that the total of all the split percentages is 100, always.
    for example:
    If we have 6 groups, and each group is 16.666666666666668%, it shows as 16.7% in the UI.
    However, 16.7 * 6 = 100.2, which is too high and will trigger validation.
    The floats can't possibly reach our validity threshold (>.99, <100.1) while maintaining 1 decimal place
      and all being the exact same float.
    So instead, we add up all the individual values, see how + or - we are from 100, and return a value
      that adds that difference to the individual value.
    Ex: 100 - 100.2 (which is 16.7*6) = -.2
    If that value is -.2, it's above 100, so adding -.2 to the individual value gives us 16.5 and a total of 100.
    If that value is .2, it's below 100, so adding .2 takes us to the full 100 (this case wont mathamatically happen for splits of 1...10)
    If that value is 0, adding it changes nothing.
    Therefore, addition covers all bases here.
  */

  // turns .16666666666666668 into 16.7
  const percentageValue = +(individualValue * 100).toFixed(1);

  // take all 16.7's and multiply by the number of groups
  const combinedIndividualValues = percentageValue * numGroups;

  // see how far we are from 100 (in this example, -.2)
  const differenceFrom100 = 100 - combinedIndividualValues;

  // convert difference to decimal (from percentage) and add it to the original individual value
  // ex: .16666666666666668 + (-.2/100) = .16466666666666668, which will convert to 16.5%
  return individualValue + differenceFrom100 / 100;
};

const redistributeEvenly = (numGroups) => {
  const individualValue = 100 / numGroups / 100;
  // The total of all the split percentages needs to be 100, so sometimes the final value must differ
  // This function is run once on adding a split group, minimizing performance concerns.
  const finalValue = getFinalValueInEvenDistribution(numGroups, individualValue);

  const evenlyDistributed = Array(numGroups)
    .fill()
    .map((val, i) => ({
      splitPercentage: splitPercentageToString(
        i === numGroups - 1 ? finalValue : individualValue, // use the final value at the last index
        1,
      ),
    }));

  return evenlyDistributed;
};

export const useSplitGroups = (initialSplitGroups, evenlyDistribute) => {
  const [splitGroups, setSplitGroups] = useState(getInitialState(initialSplitGroups ?? []));

  useEffect(() => {
    if (evenlyDistribute === CHECKBOX_STATUS.CHECKED) {
      const replaceSplitGroups = redistributeEvenly(splitGroups.length);
      setSplitGroups(replaceSplitGroups);
    }
  }, [evenlyDistribute]);

  const onAdd = () => {
    if (evenlyDistribute === CHECKBOX_STATUS.CHECKED) {
      const replaceSplitGroups = redistributeEvenly(splitGroups.length + 1);
      setSplitGroups(replaceSplitGroups);
    } else {
      setSplitGroups((state) => [...state, { splitPercentage: splitPercentageToString(0) }]);
    }
  };

  const onRemove = (index) => {
    if (evenlyDistribute === CHECKBOX_STATUS.CHECKED) {
      const replaceSplitGroups = redistributeEvenly(splitGroups.length - 1);
      setSplitGroups(replaceSplitGroups);
    } else {
      setSplitGroups((state) => [...state.filter((_, i) => i !== index)]);
    }
  };

  const onUpdate = (val, index) => {
    // no more than 2 decimal places allowed
    if (isValidSplitPercentage(val)) {
      setSplitGroups((state) => {
        const item = {
          ...splitGroups[index],
          splitPercentage: val,
        };
        return [...state.slice(0, index), item, ...state.slice(index + 1)];
      });
    }
  };
  return { splitGroups, onAdd, onRemove, onUpdate };
};
